import React, { useEffect, useMemo, useState } from 'react';

const emptyItem = () => ({ title: '', body: '', date: new Date().toISOString().slice(0, 10), pinned: false });
const deepClone = (o) => JSON.parse(JSON.stringify(o));

export default function NewsAdmin() {
  const [items, setItems] = useState([]);
  const [original, setOriginal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [draft, setDraft] = useState(emptyItem());
  const [query, setQuery] = useState('');

  const isDirty = useMemo(() => JSON.stringify(original) !== JSON.stringify(items), [original, items]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/news');
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setItems(deepClone(data));
      setOriginal(deepClone(data));
    } catch (e) {
      setError(e.message || 'Failed to load news.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const publish = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch('/api/news', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(items),
      });
      if (!res.ok) throw new Error(await res.text());
      const saved = await res.json();
      setItems(deepClone(saved));
      setOriginal(deepClone(saved));
      setMessage('Published successfully.');
    } catch (e) {
      setError(e.message || 'Failed to publish.');
    } finally {
      setSaving(false);
    }
  };

  const validate = () => {
    if (!draft.title.trim()) return 'Title is required.';
    if (!draft.body.trim()) return 'Body is required.';
    if (!draft.date.trim()) return 'Date is required.';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.date.trim())) return 'Date must be YYYY-MM-DD.';
    return '';
  };

  const saveDraft = () => {
    const err = validate();
    if (err) { alert(err); return; }
    if (editingIndex === -1) {
      setItems((prev) => [{ ...draft }, ...prev]);
    } else {
      setItems((prev) => prev.map((item, i) => i === editingIndex ? { ...draft } : item));
    }
    setEditingIndex(null);
    setDraft(emptyItem());
  };

  const deleteItem = (idx) => {
    if (!window.confirm(`Delete "${items[idx].title}"?`)) return;
    setItems((prev) => prev.filter((_, i) => i !== idx));
    if (editingIndex === idx) { setEditingIndex(null); setDraft(emptyItem()); }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((n) => !q || `${n.title} ${n.body}`.toLowerCase().includes(q));
  }, [items, query]);

  if (loading) return <div style={{ padding: 20 }}>Loading…</div>;

  return (
    <div>
      <div className="admin-header">
        <div>
          <h2>News &amp; Announcements</h2>
          <p>Manage community news posts and announcements.</p>
        </div>
        <div className="admin-actions">
          <button className="btn btn-outline btn-sm" onClick={load} disabled={saving}>Reload</button>
          <button className="btn btn-outline btn-sm" onClick={() => { setEditingIndex(-1); setDraft(emptyItem()); }} disabled={saving}>+ Add post</button>
          <button className="btn btn-primary btn-sm" onClick={publish} disabled={!isDirty || saving}>
            {saving ? 'Publishing…' : 'Publish changes'}
          </button>
        </div>
      </div>

      {error && <div className="admin-msg-error">{error}</div>}
      {message && <div className="admin-msg-success">{message}</div>}

      {editingIndex !== null && (
        <div className="admin-form">
          <h3>{editingIndex === -1 ? 'Add post' : 'Edit post'}</h3>
          <div className="admin-form-grid">
            <label className="full-width">
              <span>Title *</span>
              <input value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} />
            </label>
            <label>
              <span>Date (YYYY-MM-DD) *</span>
              <input value={draft.date} onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))} />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500 }}>
              <input
                type="checkbox"
                checked={draft.pinned}
                onChange={(e) => setDraft((d) => ({ ...d, pinned: e.target.checked }))}
                style={{ width: 'auto' }}
              />
              Pin to top
            </label>
            <label className="full-width">
              <span>Body *</span>
              <textarea rows={5} value={draft.body} onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))} />
            </label>
          </div>
          <div className="admin-form-actions">
            <button className="btn btn-outline btn-sm" onClick={() => { setEditingIndex(null); setDraft(emptyItem()); }}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={saveDraft}>Save</button>
          </div>
        </div>
      )}

      <div className="admin-filters">
        <input placeholder="Search posts…" value={query} onChange={(e) => setQuery(e.target.value)} />
      </div>

      <div className="admin-list">
        {filtered.length === 0
          ? <p style={{ color: 'var(--text-muted)' }}>No posts found.</p>
          : filtered.map((n) => {
              const masterIdx = items.indexOf(n);
              return (
                <div className="admin-item" key={`${n.title}-${masterIdx}`}>
                  <div className="admin-item-info">
                    <div className="admin-item-title">{n.title || '(Untitled)'}</div>
                    <div className="admin-item-meta">
                      {n.date && <span>{n.date}</span>}
                      {n.pinned && <span className="badge badge-pinned">Pinned</span>}
                    </div>
                    {n.body && <div className="admin-item-body">{n.body.slice(0, 150)}{n.body.length > 150 ? '…' : ''}</div>}
                  </div>
                  <div className="admin-item-btns">
                    <button className="btn btn-outline btn-sm" onClick={() => { setEditingIndex(masterIdx); setDraft({ ...n }); }}>Edit</button>
                    <button className="btn btn-outline btn-sm" onClick={() => deleteItem(masterIdx)}>Delete</button>
                  </div>
                </div>
              );
            })
        }
      </div>
    </div>
  );
}
